import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Upload, FileSpreadsheet, FileText, CheckCircle2, XCircle, X, BarChart3, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { importUnified, getWclHealth } from "@/lib/api";
import { useTeams } from "@/lib/useTeams";

const FILE_TYPES = {
  rpb: { label: "RPB Excel", icon: FileSpreadsheet, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
  data_analysis: { label: "Data Analysis", icon: BarChart3, color: "text-green-400", bg: "bg-green-500/10 border-green-500/20" },
  combat_log: { label: "Combat Log", icon: FileText, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20" },
  unknown: { label: "Unknown", icon: Upload, color: "text-muted-foreground", bg: "bg-card/50 border-border/30" },
};

function detectFileType(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".txt")) return "combat_log";
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    if (name.includes("combatanal") || name.includes("data analysis") || name.includes("dataanalysis")) {
      return "data_analysis";
    }
    return "rpb";
  }
  return "unknown";
}

function FileChip({ file, detectedType, onRemove, index }) {
  const config = FILE_TYPES[detectedType] || FILE_TYPES.unknown;
  const Icon = config.icon;
  return (
    <div data-testid={`file-chip-${index}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${config.bg} transition-all`}>
      <Icon className={`h-4 w-4 flex-shrink-0 ${config.color}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-[10px] ${config.color} border-current`}>
            {config.label}
          </Badge>
          <span className="text-[10px] text-muted-foreground">
            {(file.size / 1024).toFixed(0)} KB
          </span>
        </div>
      </div>
      <button data-testid={`remove-file-${index}`} onClick={() => onRemove(index)}
        className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-destructive/20 transition-colors flex-shrink-0">
        <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
      </button>
    </div>
  );
}

export default function ImportPage({ team, onImported }) {
  const teams = useTeams();
  const [files, setFiles] = useState([]);
  const [wclText, setWclText] = useState("");
  const [importTeam, setImportTeam] = useState(team || "");
  const [raidDate, setRaidDate] = useState(new Date().toISOString().split("T")[0]);
  const [raidName, setRaidName] = useState("");
  const [bossName, setBossName] = useState("");
  const [raidFilter, setRaidFilter] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [wclHealth, setWclHealth] = useState(null); // null | {ok, reason?}
  const fileRef = useRef(null);

  // Auto-select first available team if none is set (e.g. fresh login with no active team)
  useEffect(() => {
    if (!importTeam && teams && teams.length > 0) {
      setImportTeam(teams[0]);
    }
  }, [teams, importTeam]);

  const addFiles = useCallback((newFiles) => {
    const fileArr = Array.from(newFiles);
    const valid = fileArr.filter((f) => {
      const ext = f.name.toLowerCase();
      return ext.endsWith(".xlsx") || ext.endsWith(".xls") || ext.endsWith(".txt");
    });
    if (valid.length < fileArr.length) {
      toast.error("Only .xlsx and .txt files are supported");
    }
    if (valid.length === 0) return;

    setFiles((prev) => [...prev, ...valid.map((f) => ({ file: f, type: detectFileType(f) }))]);
    setResult(null);

    // Auto-fill raid name from first RPB file
    if (!raidName) {
      const rpb = valid.find((f) => detectFileType(f) === "rpb");
      if (rpb) {
        const name = rpb.name.replace(/\.[^/.]+$/, "").replace(/_/g, " ").substring(0, 80);
        setRaidName(name);
      }
    }
  }, [raidName]);

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Parse WCL textarea into a clean array of URLs / codes (one per non-empty line)
  const parseWclUrls = () => wclText.split(/[\r\n]+/).map((s) => s.trim()).filter(Boolean);

  const checkWclHealth = useCallback(() => {
    getWclHealth().then(setWclHealth).catch(() => setWclHealth({ ok: false, reason: "Network error" }));
  }, []);

  const handleImport = async () => {
    const wclUrls = parseWclUrls();
    if (files.length === 0 && wclUrls.length === 0) {
      toast.error("Add at least one WarcraftLogs link or one file");
      return;
    }
    if (!raidName.trim()) { toast.error("Enter a raid name"); return; }
    if (!importTeam) { toast.error("Select a team"); return; }

    setImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f.file));
      wclUrls.forEach((u) => formData.append("wcl_urls", u));
      formData.append("team", importTeam);
      formData.append("date", raidDate);
      formData.append("raid_name", raidName.trim());
      formData.append("boss_name", bossName);
      if (raidFilter && raidFilter !== "all") formData.append("raid_filter", raidFilter);

      const res = await importUnified(formData);
      setResult(res);

      const fileOk = res.files?.filter((f) => f.status === "success").length || 0;
      const wclOk = res.wcl?.filter((w) => w.status === "success").length || 0;
      const playerCount = res.total_players?.length || 0;
      toast.success(`Imported ${wclOk} WCL report(s) + ${fileOk} file(s) — ${playerCount} player(s)`);
      onImported();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Import failed";
      if (Array.isArray(detail)) {
        msg = detail.map((d) => `${(d.loc || []).slice(1).join(".")}: ${d.msg}`).join("; ") || msg;
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
    }
    setImporting(false);
  };

  const typeCounts = files.reduce((acc, f) => {
    acc[f.type] = (acc[f.type] || 0) + 1;
    return acc;
  }, {});

  const hasCombatLog = files.some((f) => f.type === "combat_log");
  const wclUrlsCount = parseWclUrls().length;
  const canSubmit = (wclUrlsCount > 0 || files.length > 0) && !!raidName.trim() && !!importTeam && !importing;

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-3xl animate-fade-in" data-testid="import-page">
      <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-2">Import Data</h1>
      <p className="text-muted-foreground mb-6">
        Drop <span className="text-purple-400 font-medium">WarcraftLogs links</span> (one per line — useful for nights that hit multiple instances) and any matching <span className="text-blue-400">RPB Excel</span>, <span className="text-green-400">Data Analysis Excel</span>, or <span className="text-orange-400">.txt combat log</span> files. Files are optional if you've pasted at least one WCL link.
      </p>

      {/* ─── WarcraftLogs Links ─── */}
      <Card className="mb-6 bg-purple-500/5 border-purple-500/30" data-testid="wcl-import-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between heading text-lg uppercase tracking-tight">
            <span className="flex items-center gap-2 text-purple-400">
              <Link2 className="h-4 w-4" /> WarcraftLogs Links
              {wclUrlsCount > 0 && (
                <Badge variant="outline" className="ml-1 text-[10px] text-purple-400 border-purple-400/40">
                  {wclUrlsCount}
                </Badge>
              )}
            </span>
            <button
              data-testid="wcl-health-btn"
              onClick={checkWclHealth}
              className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            >
              {wclHealth === null ? "Check API status" : wclHealth.ok ? "✓ API OK" : "✗ API NOT OK"}
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {wclHealth && !wclHealth.ok && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-sm px-3 py-2 font-mono" data-testid="wcl-health-error">
              {wclHealth.reason}
              <div className="text-[10px] text-muted-foreground mt-1 normal-case font-sans">
                Add <code>WCL_CLIENT_ID</code> and <code>WCL_CLIENT_SECRET</code> to <code>/app/backend/.env</code>.
                Get them at <a href="https://www.warcraftlogs.com/api/clients/" target="_blank" rel="noreferrer" className="underline">warcraftlogs.com/api/clients</a>.
              </div>
            </div>
          )}
          {wclHealth?.ok && (
            <div className="text-[10px] font-mono text-green-500 bg-green-500/10 border border-green-500/30 rounded-sm px-3 py-1.5" data-testid="wcl-health-ok">
              Connected · client {wclHealth.client_id_prefix}
            </div>
          )}
          <Textarea
            data-testid="wcl-urls-textarea"
            value={wclText}
            onChange={(e) => setWclText(e.target.value)}
            placeholder={`One link per line, e.g.\nhttps://www.warcraftlogs.com/reports/aBcDeF123…   (SSC)\nhttps://fresh.warcraftlogs.com/reports/xYz456…    (TK)`}
            rows={4}
            className="bg-card/50 font-mono text-sm resize-y"
          />
          <p className="text-[10px] text-muted-foreground">
            Each link pulls encounters, kill/wipe status, DPS/HPS, deaths, interrupts, and parse %. Multiple links land into the same raid (handy for nights that hit SSC + TK). Supports both <code>www.warcraftlogs.com</code> and <code>fresh.warcraftlogs.com</code> (TBC Anniversary).
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-border/40" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">+ optional files</span>
        <div className="flex-1 h-px bg-border/40" />
      </div>

      {/* Drop Zone (optional) */}
      <div
        data-testid="file-drop-zone"
        className={`upload-zone mb-6 ${dragOver ? "dragover" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.txt"
          multiple
          className="hidden"
          data-testid="file-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drop files here or click to browse (optional)</p>
          <p className="text-xs text-muted-foreground">
            <span className="text-blue-400">.xlsx RPB</span>, <span className="text-green-400">.xlsx Data Analysis</span>, <span className="text-orange-400">.txt Combat Logs</span> — pair them with the WCL links above for the full picture.
          </p>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="file-list">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                {files.length} file(s)
              </span>
              {Object.entries(typeCounts).map(([type, count]) => {
                const config = FILE_TYPES[type];
                return (
                  <Badge key={type} variant="outline" className={`text-[10px] ${config?.color || ""}`}>
                    {config?.label || type}: {count}
                  </Badge>
                );
              })}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFiles([])}
              className="text-xs text-muted-foreground h-6">
              Clear All
            </Button>
          </div>
          {files.map((f, i) => (
            <FileChip key={`${f.file.name}-${i}`} file={f.file} detectedType={f.type}
              onRemove={removeFile} index={i} />
          ))}
        </div>
      )}

      {/* Raid Config */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Team</Label>
          <Select value={importTeam} onValueChange={setImportTeam}>
            <SelectTrigger data-testid="import-team-select" className="bg-card/50"><SelectValue /></SelectTrigger>
            <SelectContent>
              {teams.map((t) => <SelectItem key={t} value={t}>{t} Team</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Raid Date</Label>
          <Input type="date" data-testid="import-date-input" value={raidDate}
            onChange={(e) => setRaidDate(e.target.value)} className="bg-card/50" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Raid Name</Label>
          <Input data-testid="import-raid-name" value={raidName}
            onChange={(e) => setRaidName(e.target.value)}
            placeholder="e.g. SSC + TK Monday" className="bg-card/50" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Boss Name (optional)</Label>
          <Input data-testid="import-boss-name" value={bossName}
            onChange={(e) => setBossName(e.target.value)}
            placeholder="e.g. Gruul the Dragonkiller" className="bg-card/50" />
        </div>
        {hasCombatLog && (
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Combat Log Filter
            </Label>
            <Select value={raidFilter} onValueChange={setRaidFilter}>
              <SelectTrigger data-testid="import-raid-filter" className="bg-card/50"><SelectValue placeholder="All encounters" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All encounters</SelectItem>
                <SelectItem value="mag_gruul">Mag & Gruul (25-man)</SelectItem>
                <SelectItem value="karazhan">Karazhan (10-man)</SelectItem>
                <SelectItem value="ssc">SSC (25-man)</SelectItem>
                <SelectItem value="tk">Tempest Keep (25-man)</SelectItem>
                <SelectItem value="hyjal">Hyjal (25-man)</SelectItem>
                <SelectItem value="bt">Black Temple (25-man)</SelectItem>
                <SelectItem value="sunwell">Sunwell (25-man)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">Filter combat log to only include bosses from this raid</p>
          </div>
        )}
      </div>

      {/* Import Button */}
      <Button data-testid="import-btn" onClick={handleImport}
        disabled={!canSubmit}
        className="rounded-sm font-bold uppercase tracking-wider bg-foreground text-background hover:bg-foreground/90 w-full md:w-auto gap-2">
        {importing ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</>
        ) : (
          <>Import {wclUrlsCount > 0 && `${wclUrlsCount} WCL`}{wclUrlsCount > 0 && files.length > 0 && " + "}{files.length > 0 && `${files.length} file${files.length > 1 ? "s" : ""}`}</>
        )}
      </Button>

      {/* Results */}
      {result && (
        <Card className="mt-6 bg-card/50 border-green-500/30" data-testid="import-result">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-green-500 heading text-lg">
              <CheckCircle2 className="h-5 w-5" /> Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Raid: <span className="text-foreground font-medium">{result.raid_name}</span>
              {" — "}{result.total_players?.length || 0} unique player(s)
            </div>

            {/* Per-WCL results */}
            {result.wcl?.length > 0 && (
              <div className="space-y-1.5" data-testid="wcl-results">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">WarcraftLogs Reports</div>
                {result.wcl.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {w.status === "success" ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                    )}
                    <span className="font-mono text-xs truncate flex-1" title={w.url}>{w.report_title || w.code}</span>
                    <Badge variant={w.status === "success" ? "default" : "destructive"} className="text-[10px]">
                      {w.status === "success" ? "OK" : "Failed"}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{w.detail}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Per-file results */}
            {result.files?.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Files</div>
                {result.files.map((f, i) => {
                  const config = FILE_TYPES[f.type] || FILE_TYPES.unknown;
                  const Icon = config.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                      <span className="font-medium truncate flex-1">{f.filename}</span>
                      <Badge variant={f.status === "success" ? "default" : "destructive"} className="text-[10px]">
                        {f.status === "success" ? "OK" : "Failed"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{f.detail}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Player list */}
            {result.total_players?.length > 0 && (
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Players</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {result.total_players.map((name) => (
                    <Badge key={name} variant="outline" className="text-xs font-mono">{name}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
